import { useEffect, useRef, useState } from 'react';

import { ApiClientError } from '../../api/http';
import { Workspace, WorkspaceSetup, firstOpenWorkspace, listWorkspaces, openWorkspace } from '../../api/workspaces';

/** What each registry state means to the customer, and whether "Open" can work right now. */
const STATE_PRESENTATION: Record<string, { label: string; note: string; openable: boolean }> = {
  created: { label: 'Setting up', note: 'Being set up. This usually takes 1–3 minutes.', openable: false },
  active: { label: 'Running', note: '', openable: true },
  sleeping: { label: 'Sleeping', note: 'Asleep to save resources. Open it and it wakes up, usually in under a minute.', openable: true },
  suspended: { label: 'Suspended', note: 'Suspended. Check Billing, or ask your organization owner.', openable: false },
};

export function workspacePresentation(state: string): { label: string; note: string; openable: boolean } {
  return STATE_PRESENTATION[state] || { label: state || 'Unknown', note: 'Not available right now.', openable: false };
}

/** Shown while a sleeping workspace starts (the hand-off answered `409 workspace_waking`). */
export const WAKING_MESSAGE = 'Waking your studio… This usually takes under a minute. It opens by itself when it is ready.';
/** How often the waking screen re-reads the workspace's state (the list route, not the rate-limited hand-off). */
export const WAKE_POLL_MS = 3_000;
/** The hand-off is asked again at most this often while the workspace is still asleep (the server's hint wins). */
export const WAKE_REOPEN_DEFAULT_MS = 15_000;
/**
 * The hand-off is never asked again sooner than this, whatever the hint: with the first open and one
 * `active` short-cut it keeps a wake at about six hand-offs a minute, well under the hand-off's limit of
 * ten per minute per person.
 */
export const WAKE_MIN_REOPEN_MS = 10_000;
/** A `429 rate_limited` while the waking screen is up waits this long when it sends no `Retry-After`. */
export const WAKE_RATE_LIMITED_DEFAULT_MS = 60_000;
/** After this long the waking screen stops and says so; the member can try again. */
export const WAKE_GIVE_UP_MS = 180_000;

/** A customer-facing sentence for a refused hand-off. The issuer's codes are canned; nothing is echoed. */
export function openFailureMessage(failure: unknown): string {
  if (failure instanceof ApiClientError) {
    switch (failure.code) {
      case 'workspace_provisioning': return 'This workspace is still being set up.';
      case 'workspace_waking': return WAKING_MESSAGE;
      case 'workspace_busy': return 'The server is busy right now, so your workspace cannot be woken yet. Try again in a few minutes.';
      case 'wake_timeout': return 'Your workspace is taking longer than usual to wake up. Try opening it again in a minute.';
      case 'workspace_sleeping': return 'This workspace is asleep and cannot be started right now. Check Billing, or ask your organization owner.';
      case 'workspace_suspended': return 'This workspace is suspended.';
      case 'workspace_not_found': return 'This workspace is not available to your account.';
      case 'rate_limited': return 'Too many attempts. Wait a minute and try again.';
      case 'handoff_not_available': return 'Sign in with your browser session to open a workspace.';
      default: return 'The workspace could not be opened. Try again, or report a problem.';
    }
  }
  return 'The workspace could not be opened.';
}

export interface WakeDependencies {
  /** Step 1+2 of the hand-off ({@link openWorkspace}); resolves once the tab is navigating. */
  open: (workspace: Workspace) => Promise<void>;
  /** `GET /v1/workspaces`: the authoritative state, polled while the workspace wakes. */
  list: () => Promise<Workspace[]>;
  /** Called once when the server says the workspace is waking (show the waking screen). */
  onWaking: () => void;
  wait: (ms: number) => Promise<void>;
  now: () => number;
}

function isCode(failure: unknown, code: string): failure is ApiClientError {
  return failure instanceof ApiClientError && failure.code === code;
}

/**
 * Open a workspace, waking it first if it is asleep (PR-I2).
 *
 * The hand-off answers `409 workspace_waking` (with `Retry-After`) when it has asked for a sleeping
 * workspace to be started. Then this polls the list route every {@link WAKE_POLL_MS} until the
 * workspace reads `active` and opens it; while it still reads `sleeping` it asks the hand-off again
 * at most every `Retry-After` seconds (which also renews the wake request). Any other refusal, a
 * `503 workspace_busy` included, is thrown for the caller to show; after {@link WAKE_GIVE_UP_MS} it
 * throws `wake_timeout`. A failed poll is not fatal: the next one is tried.
 *
 * The list can read `active` before the workspace is ready (the wake job claims the slot first, then
 * starts the container and installs its route); the hand-off then answers `workspace_waking` again with a
 * short `Retry-After`. After such an answer `active` no longer short-cuts the wait: the hand-off is asked
 * again only when its `Retry-After` has passed, so the poll never turns into a hand-off every few seconds.
 */
export async function openWithWake(workspace: Workspace, deps: WakeDependencies): Promise<void> {
  const started = deps.now();
  let announced = false;
  let openedOnActive = false;
  for (;;) {
    let waking: ApiClientError;
    let limited = false;
    try {
      await deps.open(workspace);
      return;
    } catch (failure) {
      // Once the waking screen is up, a rate-limit answer is waited out like a waking one, within the deadline.
      limited = announced && isCode(failure, 'rate_limited');
      if (!isCode(failure, 'workspace_waking') && !limited) throw failure;
      waking = failure as ApiClientError;
    }
    if (!announced) {
      announced = true;
      deps.onWaking();
    }
    const hintMs = waking.retryAfterSeconds
      ? waking.retryAfterSeconds * 1000
      : (limited ? WAKE_RATE_LIMITED_DEFAULT_MS : WAKE_REOPEN_DEFAULT_MS);
    const reopenAt = deps.now() + Math.max(hintMs, WAKE_MIN_REOPEN_MS);
    // Refused although the list already read `active` (still starting), or rate limited: wait out the hint.
    const ignoreActive = openedOnActive || limited;
    for (;;) {
      if (deps.now() - started >= WAKE_GIVE_UP_MS) {
        throw new ApiClientError('http', 409, 'wake_timeout', 'the workspace did not wake in time');
      }
      await deps.wait(WAKE_POLL_MS);
      let ready = false;
      try {
        const targetRow = (await deps.list()).find((row) => row.workspace_id === workspace.workspace_id);
        ready = Boolean(targetRow && isReady(targetRow));
      } catch {
        ready = false;
      }
      if (ready && !ignoreActive) {
        openedOnActive = true;
        break;
      }
      if (deps.now() >= reopenAt) {
        openedOnActive = ready;
        break;
      }
    }
  }
}

// ---------------------------------------------------------------------------------------------
// Setting up a new workspace (F-53, F-54): a friendly progress screen that polls the server's real
// setup state, and says plainly when setup is refused, stalled or broken. It never claims success:
// "ready" means the list route reads the workspace `active`, and only then does it open.
// ---------------------------------------------------------------------------------------------

/** Where help goes when setup cannot finish by itself. */
export { SUPPORT_EMAIL } from '../../lib/support';
import { SUPPORT_EMAIL } from '../../lib/support';
/** A job still waiting for a worker after this long has stalled (no worker running, or the queue is stuck). */
export const SETUP_STALL_QUEUED_SECONDS = 5 * 60;
/** A job a worker holds for longer than this is taking far longer than it should. */
export const SETUP_STALL_WORKING_SECONDS = 15 * 60;
/** Roughly how long setup usually takes, for the ETA line. */
export const SETUP_TYPICAL_SECONDS = 180;
/** Inside a running job, the first minute is "creating" and the rest "starting" (a rough split for the progress line). */
export const SETUP_CREATING_SECONDS = 60;
/** Polling backoff: first check, growth factor, and the ceilings while healthy and while stalled or retrying. */
export const SETUP_POLL_FIRST_MS = 3_000;
export const SETUP_POLL_FACTOR = 1.5;
export const SETUP_POLL_MAX_MS = 15_000;
export const SETUP_POLL_SLOW_MS = 30_000;
/** The page stops polling after this long and offers "Check again". */
export const SETUP_POLL_GIVE_UP_MS = 30 * 60_000;
/** How often the friendly line under the spinner changes. */
export const SETUP_LINE_ROTATE_MS = 4_000;

export const SETUP_STEPS = ['Queued', 'Creating', 'Starting', 'Ready'] as const;

/** The friendly lines that rotate under the spinner while setup is running. */
export const SETUP_LINES = [
  'Configuring your studio…',
  'Preparing the space where all your projects will live…',
  'Setting up your writing and planning tools…',
  'Getting your private storage ready…',
] as const;

export type SetupPhase = 'queued' | 'creating' | 'starting' | 'stalled' | 'retrying' | 'failed';

export interface SetupView {
  phase: SetupPhase;
  /** Index into {@link SETUP_STEPS} of the step in progress. */
  step: number;
  title: string;
  detail: string;
  /** A rough time estimate, or '' when one would be a guess. */
  eta: string;
  /** Show the spinner: something is actively happening. */
  busy: boolean;
  /** Setup will not finish by itself: stop polling and offer "Check again". */
  terminal: boolean;
  /** Point the customer at support. */
  contact: boolean;
}

function minutes(seconds: number): string {
  const whole = Math.max(1, Math.round(seconds / 60));
  return whole === 1 ? 'a minute' : `${whole} minutes`;
}

function etaFor(ageSeconds: number): string {
  const remaining = SETUP_TYPICAL_SECONDS - ageSeconds;
  if (remaining > 90) return 'This usually takes 1–3 minutes.';
  if (remaining > 30) return 'About a minute left.';
  return 'Almost ready…';
}

const RETRY_COPY: Record<string, { title: string; detail: string }> = {
  host_at_capacity: {
    title: 'Our servers are full right now',
    detail: 'Your studio is reserved and we will keep trying automatically. It opens as soon as space frees up.',
  },
  workspace_unhealthy: {
    title: 'Your studio did not start cleanly',
    detail: 'We are trying again automatically. You do not need to do anything.',
  },
};
const RETRY_FALLBACK = {
  title: 'Setup hit a snag',
  detail: 'We are trying again automatically. You do not need to do anything.',
};

const FAILED_COPY: Record<string, { title: string; detail: string }> = {
  host_at_capacity: {
    title: 'We could not set up your studio: our servers are full',
    detail: 'Nothing is lost and you have not been charged twice. Please try again later, or contact us and we will make room.',
  },
  not_entitled: {
    title: 'Your plan does not include a studio right now',
    detail: 'Check your plan in Billing. If you think this is wrong, contact us.',
  },
  workspace_unhealthy: {
    title: 'Your studio was created but did not start',
    detail: 'This needs a person to look at it. Contact us and we will get it running.',
  },
  setup_unavailable: {
    title: 'Setup could not finish',
    detail: 'This is a problem on our side, not something you did. Contact us and we will sort it out.',
  },
};

/**
 * What to tell the customer about a workspace that is still `created`.
 *
 * `observedSeconds` is how long this page has watched it; it stands in for the server's
 * `age_seconds` only when the server sent no setup status at all.
 */
export function describeSetup(workspace: Workspace, observedSeconds = 0): SetupView {
  const setup: WorkspaceSetup | undefined = workspace.setup;
  const age = setup ? setup.age_seconds : Math.max(0, Math.floor(observedSeconds));
  const running = (phase: SetupPhase, step: number, title: string, detail: string): SetupView => ({
    phase, step, title, detail, eta: etaFor(age), busy: true, terminal: false, contact: false,
  });
  const stalled = (title: string, detail: string, step: number): SetupView => ({
    phase: 'stalled', step, title, detail, eta: '', busy: false, terminal: false, contact: true,
  });
  const failed = (copy: { title: string; detail: string }): SetupView => ({
    phase: 'failed', step: 0, ...copy, eta: '', busy: false, terminal: true, contact: true,
  });

  if (!setup) {
    // The server could not say where setup stands: show progress, and admit it if it drags on.
    if (age >= SETUP_STALL_WORKING_SECONDS) {
      return stalled(
        'Setup is taking much longer than usual',
        `We have been waiting ${minutes(age)}. We will keep checking, and open your studio as soon as it is ready.`,
        1,
      );
    }
    return running(age < SETUP_CREATING_SECONDS ? 'creating' : 'starting', age < SETUP_CREATING_SECONDS ? 1 : 2,
      'We are preparing your private studio', 'We will open it automatically when it is ready.');
  }
  switch (setup.stage) {
    case 'queued':
      if (age >= SETUP_STALL_QUEUED_SECONDS) {
        return stalled(
          'Setup has not started yet',
          `Your request is saved, but our setup service has not picked it up after ${minutes(age)}. `
            + 'This is not something you did. We will keep checking and open your studio as soon as it is ready.',
          0,
        );
      }
      return running('queued', 0, 'Your studio is next in line',
        'We are about to build your private studio. We will open it automatically when it is ready.');
    case 'working':
      if (age >= SETUP_STALL_WORKING_SECONDS) {
        return stalled(
          'Setup is taking much longer than usual',
          `We have been building your studio for ${minutes(age)}. We will keep checking and open it as soon as it is ready.`,
          2,
        );
      }
      return age < SETUP_CREATING_SECONDS
        ? running('creating', 1, 'Creating your private studio', 'We will open it automatically when it is ready.')
        : running('starting', 2, 'Starting your studio', 'Almost there: we are switching everything on.');
    case 'retrying': {
      const copy = RETRY_COPY[setup.reason || ''] || RETRY_FALLBACK;
      return {
        phase: 'retrying', step: 1, ...copy, eta: '', busy: true, terminal: false,
        contact: setup.reason === 'host_at_capacity' || age >= SETUP_STALL_QUEUED_SECONDS,
      };
    }
    case 'unknown':
      return running('starting', 2, 'Checking on your studio', 'We are confirming it is ready. We will open it automatically.');
    case 'not_queued':
      return failed({
        title: 'Setup did not start',
        detail: 'We could not start setting up your studio. Check again in a minute; if it still has not started, contact us.',
      });
    case 'failed':
    default:
      return failed(FAILED_COPY[setup.reason || ''] || FAILED_COPY.setup_unavailable);
  }
}

/** A plain-language sentence for a refused "Set up workspace" (the first-open POST). */
export function setupFailureMessage(failure: unknown): string {
  if (failure instanceof ApiClientError) {
    switch (failure.code) {
      case 'workspace_not_entitled':
        return 'Your plan does not include a studio yet. Choose a plan in Billing, then set it up here.';
      case 'workspace_limit_reached':
        return `Your plan's studio limit is reached. Contact ${SUPPORT_EMAIL} if you need another.`;
      case 'workspace_provisioning_unavailable':
        return `Studio setup is not available right now. Try again in a few minutes; if it keeps happening, contact ${SUPPORT_EMAIL}.`;
      case 'rate_limited':
        return 'Too many attempts. Wait a minute and try again.';
      default:
        return failure.message || 'Studio setup is not available right now. Try again in a few minutes.';
    }
  }
  return 'Studio setup is not available right now. Try again in a few minutes.';
}

/**
 * A row the setup screen owns: being created; asleep because its start is retrying or failed; or
 * `active` while a setup job still holds it (the slot is claimed before the container is ready).
 */
export function inSetup(workspace: Workspace): boolean {
  if (workspace.state === 'created') return true;
  // The server could not read the job queue for an `active` row: that is unknown, not "in setup".
  // The row stays usable (see UNCONFIRMED_NOTE) but is never `ready`, so it never opens by itself.
  if (statusUnconfirmed(workspace)) return false;
  return (workspace.state === 'sleeping' || workspace.state === 'active') && Boolean(workspace.setup);
}

/** An `active` row whose setup status the server could not confirm (job queue unreadable). */
export function statusUnconfirmed(workspace: Workspace): boolean {
  return workspace.state === 'active' && workspace.setup?.stage === 'unknown';
}

/** Soft note on an `active` row whose status could not be confirmed; never a success claim. */
export const UNCONFIRMED_NOTE = "We couldn't confirm your studio's status just now. You can still try opening it.";

/** Ready means the server reads it `active` and no setup job holds it any more. */
export function isReady(workspace: Workspace): boolean {
  return workspace.state === 'active' && !workspace.setup;
}

/** A row the setup screen should keep watching (setup can still finish by itself). */
export function watchable(workspace: Workspace): boolean {
  return inSetup(workspace) && !describeSetup(workspace).terminal;
}

/** Of the workspaces that were in setup (`before`), the first the server now reads `active`, if any. */
export function finishedSetup(before: ReadonlySet<string>, rows: Workspace[]): Workspace | undefined {
  return rows.find((row) => before.has(row.workspace_id) && isReady(row));
}

export type SetupPollOutcome =
  | { outcome: 'ready'; workspace: Workspace }
  | { outcome: 'failed' | 'gone' | 'gave_up' | 'cancelled' };

export interface SetupPollDependencies {
  /** `GET /v1/workspaces`: the authoritative state. */
  list: () => Promise<Workspace[]>;
  wait: (ms: number) => Promise<void>;
  now: () => number;
  /** Every successful read, so the screen shows the latest status. */
  onRows: (rows: Workspace[]) => void;
  cancelled: () => boolean;
}

/**
 * Poll the list route with backoff while `workspaceId` is being set up.
 *
 * Resolves `ready` once the server reads it `active` (only then), `failed` once setup cannot finish by
 * itself, `gone` if it disappears or moves to another state, and `gave_up` after
 * {@link SETUP_POLL_GIVE_UP_MS}. A failed read is not fatal: it waits longer and reads again.
 */
export async function pollSetup(workspaceId: string, deps: SetupPollDependencies): Promise<SetupPollOutcome> {
  const started = deps.now();
  let delay = SETUP_POLL_FIRST_MS;
  for (;;) {
    if (deps.now() - started >= SETUP_POLL_GIVE_UP_MS) return { outcome: 'gave_up' };
    await deps.wait(delay);
    if (deps.cancelled()) return { outcome: 'cancelled' };
    let rows: Workspace[];
    try {
      rows = await deps.list();
    } catch {
      delay = Math.min(delay * 2, SETUP_POLL_SLOW_MS);
      continue;
    }
    if (deps.cancelled()) return { outcome: 'cancelled' };
    deps.onRows(rows);
    const row = rows.find((candidate) => candidate.workspace_id === workspaceId);
    if (!row) return { outcome: 'gone' };
    if (isReady(row)) return { outcome: 'ready', workspace: row };
    if (!inSetup(row)) return { outcome: 'gone' };
    const view = describeSetup(row, (deps.now() - started) / 1000);
    if (view.terminal) return { outcome: 'failed' };
    const ceiling = view.phase === 'stalled' || view.phase === 'retrying' ? SETUP_POLL_SLOW_MS : SETUP_POLL_MAX_MS;
    delay = Math.min(Math.round(delay * SETUP_POLL_FACTOR), ceiling);
  }
}

/** Poll until ready, then open the workspace (the hand-off). Returns the poll's outcome. */
export async function watchSetupAndOpen(
  workspaceId: string,
  deps: SetupPollDependencies & { open: (workspace: Workspace) => Promise<void> | void },
): Promise<SetupPollOutcome> {
  const result = await pollSetup(workspaceId, deps);
  if (result.outcome !== 'ready') return result;
  // `onRows` may have unmounted the screen; a page the person has left never navigates.
  if (deps.cancelled()) return { outcome: 'cancelled' };
  await deps.open(result.workspace);
  return result;
}

function TechnicalDetails({ workspace }: { workspace: Workspace }) {
  return (
    <details className="workspace-details">
      <summary>Technical details</summary>
      <p className="settings-muted">Address: <code>{workspace.ingress_host}</code></p>
    </details>
  );
}

/** Shown when the page stopped checking by itself (the poll deadline passed); nothing is claimed about setup. */
export const SETUP_PAUSED_NOTE = 'We stopped checking automatically. Use Check again to see the latest status.';

export function WorkspaceSetupCard({
  workspace, view: raw, line = SETUP_LINES[0], checking = false, paused = false, onCheckAgain, onTryAgain,
  error = '', waking = false,
}: {
  /** Why the last "Try again" (wake) was refused, in plain language. */
  error?: string;
  /** "Try again" asked for it to be started and it is waking now. */
  waking?: boolean;
  workspace: Workspace;
  view: SetupView;
  line?: string;
  checking?: boolean;
  /** The page is no longer polling this workspace: no spinner, and offer "Check again". */
  paused?: boolean;
  onCheckAgain?: () => void;
  /** A sleeping workspace whose start failed: "Try again" asks for it to be started (the wake path). */
  onTryAgain?: () => void;
}) {
  const view = paused && !raw.terminal ? { ...raw, busy: false, eta: '', contact: true } : raw;
  const showCheck = Boolean(onCheckAgain) && (view.terminal || view.phase === 'stalled' || paused);
  const showTry = Boolean(onTryAgain) && workspace.state === 'sleeping' && view.terminal;
  return (
    <li className="project-card workspace-setup" data-phase={view.phase}>
      <span className="badge">
        {view.terminal ? 'Needs attention' : workspace.state === 'sleeping' ? 'Waiting to start' : 'Setting up'}
      </span>
      <h3>Your private studio</h3>
      <div role={view.terminal ? 'alert' : 'status'} aria-live={view.terminal ? undefined : 'polite'}
        className="workspace-setup-status">
        {view.busy ? <span className="setup-spinner" aria-hidden="true" /> : null}
        <strong>{view.title}</strong>
        <p>{view.detail}</p>
        {view.busy ? <p className="settings-muted">{line}</p> : null}
        {view.eta ? <p className="settings-muted">{view.eta}</p> : null}
        {paused && !raw.terminal ? <p className="settings-muted">{SETUP_PAUSED_NOTE}</p> : null}
      </div>
      {!view.terminal ? (
        <ol className="setup-steps" aria-label="Setup progress">
          {SETUP_STEPS.map((label, index) => (
            <li key={label} className={index < view.step ? 'done' : index === view.step ? 'current' : undefined}
              aria-current={index === view.step ? 'step' : undefined}>{label}</li>
          ))}
        </ol>
      ) : null}
      {view.contact ? (
        <p className="settings-muted">Need help? Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.</p>
      ) : null}
      {showTry ? (
        <button type="button" disabled={checking} onClick={onTryAgain}>{waking ? 'Starting…' : 'Try again'}</button>
      ) : null}
      {waking ? <p role="status" aria-live="polite" className="settings-muted">{WAKING_MESSAGE}</p> : null}
      {error ? <p role="alert" className="error">{error}</p> : null}
      {showCheck ? (
        <button type="button" className="secondary" disabled={checking} onClick={onCheckAgain}>
          {checking ? 'Checking…' : 'Check again'}
        </button>
      ) : null}
      <TechnicalDetails workspace={workspace} />
    </li>
  );
}

export function WorkspaceList({
  workspaces,
  opening,
  errors,
  onOpen,
  waking = '',
  ready = '',
  observedSeconds = {},
  line,
  checking = false,
  paused = {},
  onCheckAgain,
}: {
  workspaces: Workspace[];
  opening: string;
  errors: Record<string, string>;
  onOpen: (workspace: Workspace) => void;
  waking?: string;
  /** The workspace that just finished setting up and is being opened automatically. */
  ready?: string;
  observedSeconds?: Record<string, number>;
  line?: string;
  checking?: boolean;
  /** Workspaces the page stopped polling (deadline passed). */
  paused?: Record<string, boolean>;
  onCheckAgain?: () => void;
}) {
  return (
    <ul className="card-grid" aria-label="Workspace list">
      {workspaces.map((workspace) => {
        if (inSetup(workspace)) {
          return (
            <WorkspaceSetupCard key={workspace.workspace_id} workspace={workspace}
              view={describeSetup(workspace, observedSeconds[workspace.workspace_id] || 0)}
              line={line} checking={checking || Boolean(opening)} paused={Boolean(paused[workspace.workspace_id])}
              onCheckAgain={onCheckAgain}
              onTryAgain={workspace.state === 'sleeping' ? () => onOpen(workspace) : undefined}
              error={errors[workspace.workspace_id] || ''} waking={waking === workspace.workspace_id} />
          );
        }
        const view = workspacePresentation(workspace.state);
        const busy = opening === workspace.workspace_id;
        return (
          <li className="project-card" key={workspace.workspace_id}>
            <span className="badge">{view.label}</span>
            <h3>Your private studio</h3>
            {view.note ? <p className="settings-muted">{view.note}</p> : null}
            {statusUnconfirmed(workspace) ? <p role="status" className="settings-muted">{UNCONFIRMED_NOTE}</p> : null}
            {ready === workspace.workspace_id && isReady(workspace) && waking !== workspace.workspace_id ? (
              <p role="status" aria-live="polite">Your studio is ready. Opening it now…</p>
            ) : null}
            <button
              type="button"
              disabled={!view.openable || Boolean(opening)}
              onClick={() => onOpen(workspace)}
            >
              {busy ? (waking === workspace.workspace_id ? 'Waking…' : 'Opening…') : 'Open studio'}
            </button>
            {waking === workspace.workspace_id ? (
              <p role="status" aria-live="polite" className="settings-muted">{WAKING_MESSAGE}</p>
            ) : null}
            {errors[workspace.workspace_id] ? (
              <p role="alert" className="error">{errors[workspace.workspace_id]} <a href="/support">Report a problem</a></p>
            ) : null}
            <TechnicalDetails workspace={workspace} />
          </li>
        );
      })}
    </ul>
  );
}

/** Refresh authoritative rows after setup; never infer an active workspace from the POST. */
export async function setUpFirstWorkspace(): Promise<Workspace[]> {
  await firstOpenWorkspace();
  return listWorkspaces();
}

/** The empty state's "Set up workspace" action and any refusal notice. Status refreshes by itself. */
export function WorkspaceSetupControls({ empty, busy, notice, onSetUp }: {
  empty: boolean; busy: boolean; notice: string; onSetUp: () => void;
}) {
  return <>
    {empty && <button type="button" disabled={busy} onClick={onSetUp}>
      {busy ? 'Setting up…' : 'Set up my studio'}
    </button>}
    {notice && <p role="alert" className="error">{notice}</p>}
  </>;
}

const WORKSPACE_EXPLAINER = 'Your studio is your private place to write, plan and create. '
  + 'Your projects live inside it.';

export function WorkspacesPanel({ heading = 'Your studio' }: { heading?: string | null } = {}) {
  const [workspaces, setWorkspaces] = useState<Workspace[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [opening, setOpening] = useState('');
  const [settingUp, setSettingUp] = useState(false);
  const [setupNotice, setSetupNotice] = useState('');
  const [configured, setConfigured] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [waking, setWaking] = useState('');
  const [ready, setReady] = useState('');
  const [checking, setChecking] = useState(false);
  const [lineIndex, setLineIndex] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  // When this page began watching each workspace being set up (for the fallback when the server sends no status).
  const watchedSince = useRef<Record<string, number>>({});
  const [watchGeneration, setWatchGeneration] = useState(0);
  // Workspaces whose poll hit its deadline: shown as "we stopped checking", with Check again.
  const [paused, setPaused] = useState<Record<string, boolean>>({});
  // False once the panel unmounts: a page the person has left never navigates to a workspace.
  const alive = useRef(true);
  // Single flight: one hand-off at a time, whichever path (poll, Check again, a click) asks first.
  const openingRef = useRef('');
  // Workspaces this page has seen not yet ready: only these open by themselves when they become ready,
  // so a running studio whose routine job briefly holds it never navigates the person away.
  const seenSettingUp = useRef<Set<string>>(new Set());
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    let active = true;
    listWorkspaces()
      .then((value) => active && setWorkspaces(value))
      .catch((failure: unknown) => {
        if (!active) return;
        if (failure instanceof ApiClientError && failure.code === 'workspace_domain_unconfigured') {
          setConfigured(false);
          setWorkspaces([]);
          return;
        }
        setLoadError(failure instanceof Error ? failure.message : 'workspaces could not be loaded');
      });
    return () => { active = false; };
  }, []);

  const settingUpId = workspaces?.find(inSetup)?.workspace_id || '';
  for (const row of workspaces || []) {
    if (inSetup(row)) seenSettingUp.current.add(row.workspace_id);
  }

  // Rotate the friendly line and tick the clock while something is being set up.
  useEffect(() => {
    if (!settingUpId) return undefined;
    const timer = globalThis.setInterval(() => {
      setLineIndex((index) => (index + 1) % SETUP_LINES.length);
      setNow(Date.now());
    }, SETUP_LINE_ROTATE_MS);
    return () => globalThis.clearInterval(timer);
  }, [settingUpId]);

  // Poll while a workspace is being set up; open it the moment the server says it is ready. A poll that
  // ends (ready, failed, gave up) is not restarted until "Check again" bumps the generation.
  useEffect(() => {
    if (!settingUpId) return undefined;
    if (watchedSince.current[settingUpId] === undefined) watchedSince.current[settingUpId] = Date.now();
    const watchedId = settingUpId;
    let cancelled = false;
    setPaused((current) => ({ ...current, [watchedId]: false }));
    void watchSetupAndOpen(watchedId, {
      list: listWorkspaces,
      wait: (ms) => new Promise((resolve) => globalThis.setTimeout(resolve, ms)),
      now: () => Date.now(),
      onRows: (rows) => setWorkspaces(rows),
      cancelled: () => cancelled || !alive.current,
      open: (row) => openWhenReady(row),
    }).then((result) => {
      if (result.outcome === 'gave_up' && !cancelled) setPaused((current) => ({ ...current, [watchedId]: true }));
    });
    return () => { cancelled = true; };
    // `open` uses only state setters and module helpers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingUpId, watchGeneration]);

  /** Setup finished (the server reads it `active`): say so and open it, unless the page was left. */
  function openWhenReady(workspace: Workspace): Promise<void> | void {
    if (!alive.current || !isReady(workspace) || !seenSettingUp.current.has(workspace.workspace_id)) return undefined;
    setReady(workspace.workspace_id);
    return open(workspace);
  }

  async function open(workspace: Workspace) {
    const workspaceId = workspace.workspace_id;
    if (openingRef.current) return;
    openingRef.current = workspaceId;
    setOpening(workspaceId);
    setErrors((current) => ({ ...current, [workspaceId]: '' }));
    try {
      await openWithWake(workspace, {
        open: (row) => openWorkspace(row.workspace_id, row.ingress_host, undefined, () => alive.current),
        list: async () => {
          const rows = await listWorkspaces();
          setWorkspaces(rows);
          return rows;
        },
        onWaking: () => {
          setWaking(workspaceId);
          setReady('');  // it read active but is still starting: no "ready" claim while it wakes
        },
        wait: (ms) => new Promise((resolve) => globalThis.setTimeout(resolve, ms)),
        now: () => Date.now(),
      });
      // The tab is navigating to the workspace; leave the button disabled until it does.
    } catch (failure) {
      if (!alive.current) return;
      openingRef.current = '';
      setErrors((current) => ({ ...current, [workspaceId]: openFailureMessage(failure) }));
      setOpening('');
      setWaking('');
      setReady('');
    }
  }

  async function checkAgain() {
    setChecking(true);
    const before = new Set((workspaces || []).filter(inSetup).map((row) => row.workspace_id));
    try {
      const rows = await listWorkspaces();
      setWorkspaces(rows);
      // A workspace that finished while the page was not polling opens just as a polled one would.
      const finished = finishedSetup(before, rows);
      if (finished) void openWhenReady(finished);
    } catch {
      // The card keeps its last status; the restarted watch reads again shortly.
    } finally {
      setChecking(false);
      setWatchGeneration((generation) => generation + 1);
    }
  }

  async function setUp() {
    setSettingUp(true);
    setSetupNotice('');
    try {
      const rows = await setUpFirstWorkspace();
      // Everything listed is new (the button shows only on an empty list): a studio that is already
      // ready opens at once, just as one that finishes while the page polls.
      for (const row of rows) seenSettingUp.current.add(row.workspace_id);
      setWorkspaces(rows);
      const readyRow = rows.find(isReady);
      if (readyRow) void openWhenReady(readyRow);
    } catch (failure) {
      setSetupNotice(setupFailureMessage(failure));
    } finally {
      setSettingUp(false);
    }
  }

  if (loadError) return <p role="alert" className="error">{loadError}</p>;
  if (!configured || workspaces === null) return null;
  const observed: Record<string, number> = {};
  for (const [id, since] of Object.entries(watchedSince.current)) observed[id] = Math.max(0, (now - since) / 1000);
  return (
    <section className="workspaces-panel" aria-labelledby={heading ? 'workspaces-heading' : undefined} aria-label={heading ? undefined : 'Your studio'}>
      {heading ? <h2 id="workspaces-heading">{heading}</h2> : null}
      {heading ? <p className="settings-muted">{WORKSPACE_EXPLAINER}</p> : null}
      <WorkspaceSetupControls empty={workspaces.length === 0} busy={settingUp || Boolean(opening)}
        notice={setupNotice} onSetUp={setUp} />
      <WorkspaceList workspaces={workspaces} opening={opening} errors={errors} onOpen={open} waking={waking}
        ready={ready} observedSeconds={observed} line={SETUP_LINES[lineIndex]} checking={checking} paused={paused}
        onCheckAgain={checkAgain} />
    </section>
  );
}
